/**
 * Route identity helpers: keep a page mounted across a language switch.
 *
 * Every localised page is registered twice in the router — `/health-assistant`
 * and `/:lang/health-assistant` — as two distinct routes. Switching language
 * navigates from one to the other, which by default unmounts the page and
 * remounts it (losing the open conversation, scroll position, an in-flight
 * stream, the composer draft). Two things must agree for React to treat the
 * page as the same instance:
 *
 *   1. the routed component has the same identity for both variants
 *      (`getStableRouteComponent`), and
 *   2. no ancestor `key` differs between them — `RoutedOutlet` keys its
 *      Suspense boundary by pathname, so it must key by the pathname *without*
 *      its language prefix (`languageAgnosticPathKey`).
 *
 * Distinct pages still get distinct keys, so the clean-remount-per-navigation
 * behaviour that fixed the "wrong page flashes on sibling routes" race is kept.
 */
import { currentLanguagePrefix } from "@shared/lib/localized-path";

/** `health-assistant-i18n` -> `health-assistant`. */
export function canonicalRouteName(routeName: string): string {
  return routeName.replace(/-i18n$/, "");
}

/** `/fr/health-assistant` and `/health-assistant` both -> `/health-assistant`. */
export function languageAgnosticPathKey(pathname: string): string {
  const prefix = currentLanguagePrefix(pathname);
  const stripped = prefix ? pathname.slice(prefix.length) : pathname;
  return stripped === "" ? "/" : stripped;
}

const stableComponents = new Map<string, unknown>();

/**
 * One component per canonical route name, shared by the unprefixed and the
 * `/:lang` variant. `factory` runs once per name; later variants reuse it.
 */
export function getStableRouteComponent<T>(
  routeName: string,
  factory: () => T,
): T {
  const key = canonicalRouteName(routeName);
  if (!stableComponents.has(key)) stableComponents.set(key, factory());
  return stableComponents.get(key) as T;
}
